import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRippleModule } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { SharedModule } from 'src/app/shared/shared.module';
import { environment } from 'src/environments/environment';
import { AvatarComponent } from 'src/app/components/avatar/avatar.component';
import { InvoicePreviewDialogComponent } from 'src/app/components/invoice-preview-dialog/invoice-preview-dialog.component';
import { AuthStore } from 'src/app/store/auth.store';
import { SupportChatStore } from './services/support-chat.store';
import { SupportChatService } from './services/support-chat.service';
import { CryptoPaymentService } from 'src/app/services/crypto-payment.service';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { RechargeTicketComponent } from './components/recharge-ticket.component';
import { AudioPlayerComponent } from './components/audio-player.component';
import { PushPermissionDialogComponent } from './components/push-permission-dialog.component';
import {
  AttachmentKind,
  ChatAttachment,
  ChatMessage,
  ChatMessageStatus,
  ChatSender,
  ChatTicket
} from './support-chat.model';
import { PageLayoutComponent } from 'src/app/components/layout/page-layout/page-layout.component';
import { SmLogoWhiteComponent } from 'src/app/components/sm-logo/sm-logo-white.component';

const MAX_FILE_BYTES = 20 * 1024 * 1024;
// Images bigger than this raw are rejected outright (decoding into a canvas
// would take too much memory). Anything smaller is attempted-compressed.
const MAX_RAW_IMAGE_BYTES = 50 * 1024 * 1024;
const IMAGE_MAX_DIMENSION = 1920;
const IMAGE_QUALITY = 0.85;
// Skip recompression if the image is already this small — not worth the CPU.
const IMAGE_COMPRESS_THRESHOLD = 400 * 1024;
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
// Recording cutoff. Telegram bots can send files up to 50MB but our backend
// caps attachments at 20MB; with OPUS at ~24kbps a voice note that long is
// roughly 110 minutes, far longer than anyone realistically records.
const MAX_RECORDING_MS = 5 * 60 * 1000;
// Candidate codecs in preference order. OGG/Opus lets the backend send the
// recording to Telegram via sendVoice (round waveform bubble) instead of
// sendAudio. Falls back to webm/opus (Chrome/Firefox default) and finally
// mp4/aac for Safari.
const RECORDER_MIMES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4'
];
// Mirrors the backend MAX_FILES_PER_MESSAGE (Telegram album limit is 10).
const MAX_FILES_PER_MESSAGE = 10;
// Window during which the customer can edit/delete their own message.
const MESSAGE_EDIT_WINDOW_MS = 60 * 1000;

type MessageSegment =
  | { kind: 'text'; value: string }
  | { kind: 'inline-code'; value: string }
  | { kind: 'code-block'; value: string };

// Icon names recognised by the welcome view's FAQ list.
type TopicIcon = 'coin' | 'percent' | 'flask' | 'receipt';

type DisplayMessage = {
  kind: 'msg';
  from: 'me' | 'them';
  sameAsPrev: boolean;
  sameAsNext: boolean;
  text?: string;
  segments: MessageSegment[];
  attachments?: ChatMessage['attachments'];
  ticket?: ChatTicket;
  replyTo?: ChatMessage['replyTo'];
  status: ChatMessageStatus;
  createdAt: string;
  editedAt?: string;
  _id?: string;
  id?: string;
  localId?: string;
};

type DisplayGroup = {
  key: string;
  label: string;
  items: DisplayMessage[];
};

const dayKey = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

// Splits a message text into typed segments:
//   ```block```   → 'code-block' (multiline supported; optional language hint
//                    on the first line is stripped, e.g. ```ts\ncode\n```)
//   `inline`      → 'inline-code'
//   anything else → 'text'
// Triple backticks are matched first so they don't get eaten by the inline rule.
// Lazy on the inner group so the *first* ``` after the opener closes the block,
// then we anchor on a non-backtick lookahead so an extra trailing backtick
// (```` instead of ```) is tolerated.
const CODE_RE = /```([\s\S]*?)```(?!`)|`([^`\n]+)`/g;

const stripLanguageHint = (code: string): string => {
  // Allow at most one short alphanumeric "language" token before the first
  // newline (typescript, py, bash, json, …). If present, drop that whole line.
  // Falls back gracefully when there's no newline (single-line code block).
  const nlIdx = code.indexOf('\n');
  if (nlIdx === -1) return code;
  const firstLine = code.slice(0, nlIdx).trim();
  if (firstLine.length > 0 && firstLine.length <= 20 && /^[a-zA-Z0-9_+\-]+$/.test(firstLine)) {
    return code.slice(nlIdx + 1);
  }
  return code;
};

const parseMessageSegments = (text: string | undefined): MessageSegment[] => {
  if (!text) return [];
  const out: MessageSegment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  CODE_RE.lastIndex = 0;
  while ((m = CODE_RE.exec(text)) !== null) {
    if (m.index > last) {
      out.push({ kind: 'text', value: text.slice(last, m.index) });
    }
    if (m[1] !== undefined) {
      let value = m[1].replace(/^\r?\n/, '').replace(/\r?\n$/, '');
      value = stripLanguageHint(value);
      out.push({ kind: 'code-block', value });
    } else if (m[2] !== undefined) {
      out.push({ kind: 'inline-code', value: m[2] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    out.push({ kind: 'text', value: text.slice(last) });
  }
  return out;
};

@Component({
  selector: 'app-support-chat',
  standalone: true,
  imports: [
    SharedModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatRippleModule,
    AvatarComponent,
    LinkifyPipe,
    RechargeTicketComponent,
    AudioPlayerComponent,
    PageLayoutComponent,
    SmLogoWhiteComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './support-chat.component.html',
  styleUrl: './support-chat.component.scss'
})
export default class SupportChatComponent implements OnInit, AfterViewInit, OnDestroy {
  private snackBar = inject(MatSnackBar);
  private location = inject(Location);
  private dialog = inject(MatDialog);
  private chatService = inject(SupportChatService);
  private cryptoPaymentService = inject(CryptoPaymentService);
  private router = inject(Router);
  authStore = inject(AuthStore);
  store = inject(SupportChatStore);

  // Fee de procesamiento traído desde SystemConfig (crypto_payment.fee_percent).
  // 1.5 es solo el fallback antes de que llegue la primera respuesta de /config;
  // la UI reacta cuando el signal cambia y la FAQ se recomputa con el valor real.
  readonly feePercent = signal(1.5);

  /** When true, renders the chat shell without the page layout (for floating use). */
  embedded = input(false);
  /** Emitted when the back/close button is pressed in embedded mode. */
  close = output<void>();

  openingAttachmentKey = signal<string | null>(null);

  // Secure blob URL cache: attachments are fetched with the Authorization
  // header and served as local blob: URLs so the auth token never leaks
  // into browser history, logs or Referer headers.
  private readonly blobUrlCache = new Map<string, string>();
  private readonly blobUrlInFlight = new Set<string>();
  private readonly blobCacheVersion = signal(0);

  // Two-screen UX inside the chat: 'welcome' is the landing (greeting +
  // common topics + resume-card); 'thread' is the actual conversation with
  // composer. Always starts on 'welcome' so the user lands on the home
  // screen each time the floating chat is opened.
  view = signal<'welcome' | 'thread'>('welcome');

  // Latest admin message preview used by the "resume conversation" card on
  // the welcome screen. Messages are stored newest-first.
  lastAdminPreview = computed(() => {
    const latest = this.store.messages().find((m) => m.sender === ChatSender.ADMIN);
    if (!latest) return '';
    if (latest.text?.trim()) return latest.text.trim();
    if (latest.attachments?.length) return '📎 Adjunto';
    return 'Mensaje del soporte';
  });

  // First word of the user's full name — used by the welcome greeting
  // ("Hola {firstName} 👋"). Falls back to a generic salutation when the
  // name isn't loaded yet.
  firstName = computed(() => {
    const full = this.authStore.userFullName() ?? '';
    const first = full.trim().split(/\s+/)[0];
    return first || '';
  });

  // Quick-action topics displayed in the welcome view's FAQ list. Tapping
  // one drops the phrase into the composer and jumps to the conversation.
  // Icon names map to inline SVGs rendered in the template.
  // FAQ items shown in the welcome view. Each card is expandable in-place:
  // tapping the question toggles the answer below it (no composer / chat
  // round-trip). Keep these aligned with the deposit-via-crypto support
  // policy — they're the first thing a customer sees before chatting.
  readonly commonTopics = computed<Array<{ icon: TopicIcon; question: string; answer: string }>>(() => {
    const fee = this.feePercent();
    // Monto que el cliente envía para recibir 100 netos: 100 / (1 - fee/100).
    // 2 decimales coincide con la granularidad del fee en SystemConfig.
    const factor = 1 - fee / 100;
    const example = factor > 0 ? (100 / factor).toFixed(2) : '101.52';
    return [
      {
        icon: 'coin',
        question: '¿Qué tipos de criptomonedas aceptan?',
        answer:
          'Por el momento aceptamos USDT en las redes TRC20 (Tron) y BEP20 (Binance Smart Chain). '
      },
      {
        icon: 'percent',
        question: '¿Cuál es el costo de procesamiento?',
        answer:
          `Cobramos un ${fee}% sobre el monto que quieres recibir en tu cuenta. ` +
          `Ejemplo: si quieres recibir 100 USD en tu cuenta, debes enviar ≈ ${example} USDT.`
      },
      {
        icon: 'flask',
        question: '¿Puedo realizar un monto de prueba?',
        answer:
          'Sí, puedes realizar una recarga de prueba, siempre que cumplas con el monto mínimo mostrado en el formulario de recarga. ' +
          'Ten en cuenta que la red blockchain cobra su propia comisión (gas) por cada transacción.'
      },
      {
        icon: 'receipt',
        question: '¿Puedo utilizar el saldo para pagos de facturas?',
        answer:
          'Sí, puede recargar su cuenta con USDT y luego utilizar su saldo SuperPay para cualquier servicio, ' +
          'pagos de facturas o cualquier otro pago en la aplicación.'
      }
    ];
  });

  // Tracks which FAQ card is currently open (by question string). Null
  // means all collapsed. Only one is open at a time so the answers don't
  // pile up and force the user to scroll past them.
  expandedTopic = signal<string | null>(null);

  toggleTopic(question: string) {
    this.expandedTopic.update((curr) => (curr === question ? null : question));
  }

  draft = signal('');
  // Multi-file composer state. Each entry tracks the original File and an
  // optional blob URL used for the chip thumbnail.
  pickedFiles = signal<Array<{ file: File; previewUrl?: string }>>([]);

  // ── voice recording state ─────────────────────────────────
  // `idle` shows the mic button, `recording` shows the elapsed timer + slide-
  // to-cancel hint, `preview` shows a playable bubble with send/discard.
  recordingState = signal<'idle' | 'recording' | 'preview'>('idle');
  // Elapsed-ms tick, updated every 250ms while recording, then frozen.
  recordingElapsedMs = signal(0);
  // Captured blob + playback URL once recording stops.
  recordingBlob = signal<Blob | null>(null);
  recordingPreviewUrl = signal<string | null>(null);
  recordingDurationSec = signal(0);

  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];
  private recordingStartedAt = 0;
  private recordingTimer: ReturnType<typeof setInterval> | null = null;
  private recordingPickedMime = '';
  // Set when the user taps the trash icon mid-recording. The MediaRecorder
  // `stop` event still fires but we drop the buffer instead of producing a
  // preview.
  private recordingCancelled = false;

  // Scroll-to-bottom FAB state — appears when the user has scrolled up and
  // new messages arrive (or just to jump back to the latest).
  showScrollFab = signal(false);
  newMessagesInFab = signal(0);

  // In-conversation search state. When the user types, search results
  // replace the regular thread view.
  searchOpen = signal(false);
  searchQuery = signal('');
  searchResults = signal<ChatMessage[]>([]);
  searching = signal(false);
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  // Edit state — when set, the composer is in edit mode for that message.
  editingMessageId = signal<string | null>(null);

  // Reverse the array so the oldest message renders at the top and newest at the bottom.
  // Decorate with sameAsPrev (consecutive messages from the same sender share style).
  // `sameAsPrev` resets on day boundaries so the day-divider sits cleanly.
  displayMessages = computed<DisplayMessage[]>(() => {
    const reversed = [...this.store.messages()].reverse();
    return reversed.map((m, i) => {
      const prev = reversed[i - 1];
      const next = reversed[i + 1];
      const senderOf = (msg: ChatMessage): 'me' | 'them' =>
        msg.sender === ChatSender.CUSTOMER ? 'me' : 'them';
      const from = senderOf(m);
      const prevFrom = prev ? senderOf(prev) : null;
      const nextFrom = next ? senderOf(next) : null;
      const sameDayPrev = !!prev && dayKey(prev.createdAt) === dayKey(m.createdAt);
      const sameDayNext = !!next && dayKey(next.createdAt) === dayKey(m.createdAt);
      const sameAsPrev = !!prev && prevFrom === from && sameDayPrev;
      const sameAsNext = !!next && nextFrom === from && sameDayNext;
      return {
        kind: 'msg' as const,
        from,
        sameAsPrev,
        sameAsNext,
        text: m.text,
        segments: parseMessageSegments(m.text),
        attachments: m.attachments,
        ticket: m.ticket,
        replyTo: m.replyTo,
        status: m.status,
        createdAt: m.createdAt,
        editedAt: m.editedAt,
        _id: m._id,
        id: m.id,
        localId: m.localId
      };
    });
  });

  // Group messages by calendar day so we can show "Hoy / Ayer / 12 may"
  // dividers between groups.
  groupedDisplay = computed<DisplayGroup[]>(() => {
    const msgs = this.displayMessages();
    if (msgs.length === 0) return [];
    const groups: DisplayGroup[] = [];
    let currentKey = '';
    for (const m of msgs) {
      const key = dayKey(m.createdAt);
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ key, label: this.formatDayLabel(new Date(m.createdAt)), items: [] });
      }
      groups[groups.length - 1].items.push(m);
    }
    return groups;
  });

  ChatSender = ChatSender;
  ChatMessageStatus = ChatMessageStatus;
  AttachmentKind = AttachmentKind;

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('composerTextarea') composerTextarea?: ElementRef<HTMLTextAreaElement>;

  private lastSeenCount = 0;
  private viewReady = false;

  // Keeps SupportChatStore.viewerInThread in sync with the local view signal.
  // The store uses this flag to flip freshly-arrived admin messages straight
  // to READ when the user is already inside the conversation (so Telegram
  // jumps DELIVERED → READ without needing a tap).
  private viewerInThreadSync = effect(() => {
    this.store.setViewerInThread(this.view() === 'thread');
  });

  // Auto-scroll on new messages. Always when the user sent it (last is "me");
  // when an admin message arrives, only if the user is already near the
  // bottom (so we don't yank them while reading history). When we *don't*
  // auto-scroll because the user is scrolled up, surface a FAB with a counter.
  private autoScrollEffect = effect(() => {
    const msgs = this.displayMessages();
    untracked(() => {
      if (!this.viewReady) return;
      const prevCount = this.lastSeenCount;
      this.lastSeenCount = msgs.length;
      if (msgs.length <= prevCount) return;
      const last = msgs[msgs.length - 1];
      if (!last) return;
      if (last.from === 'me' || this.isNearBottom(140)) {
        this.afterRenderSettled(() => this.scrollToBottom(true));
      } else {
        const delta = msgs.length - prevCount;
        this.newMessagesInFab.update((c) => c + delta);
        this.showScrollFab.set(true);
      }
    });
  });

  ngOnInit() {
    this.store.ensureStream();
    void this.loadCryptoFeeConfig();
    this.maybePromptForPushPermission();
  }

  // Opens the Web Push opt-in modal every time the user lands on the chat
  // page as long as the browser permission is still `default`. We rely on
  // `Notification.permission` (synchronous, authoritative) rather than the
  // SwPush observable: if the user hasn't granted permission yet there can't
  // be an active subscription, so checking the permission state alone is
  // enough — and it sidesteps a previous bug where awaiting the SwPush
  // subscription Subject would hang and the dialog never appeared.
  private maybePromptForPushPermission() {
    if (typeof Notification === 'undefined') {
      console.debug('[push] dialog skipped: Notification API not available');
      return;
    }
    const perm = Notification.permission;
    if (perm !== 'default') {
      console.debug(`[push] dialog skipped: permission already "${perm}"`);
      return;
    }
    // Small delay so the welcome view paints before the modal layers on top,
    // otherwise the dialog appears before the user has any context about
    // where they are.
    setTimeout(() => {
      // Re-check just before opening in case the user resolved the prompt
      // through another tab during the delay.
      if (Notification.permission !== 'default') return;
      this.dialog.open(PushPermissionDialogComponent, {
        panelClass: 'push-permission-dialog',
        autoFocus: false,
        restoreFocus: false,
        width: '380px',
        maxWidth: '92vw'
      });
    }, 800);
  }

  // Fetch del fee_percent de crypto desde SystemConfig. Si falla, el signal
  // mantiene el fallback (1.5) y la FAQ sigue siendo coherente; el form de
  // recarga valida server-side de todas formas.
  private async loadCryptoFeeConfig() {
    try {
      const res = await this.cryptoPaymentService.getConfig();
      const cfg = res?.data;
      if (cfg && Number.isFinite(cfg.feePercent)) {
        this.feePercent.set(cfg.feePercent);
      }
    } catch {
      /* swallow — fallback 1.5 cubre */
    }
  }

  ngAfterViewInit() {
    void this.store.loadInitial().then(() => {
      this.lastSeenCount = this.displayMessages().length;
      this.viewReady = true;
      // Push-notification deep link: when the URL carries `?openChat=true`
      // (same flag the widget already uses to auto-open itself) skip the
      // welcome screen and drop the user straight into the conversation.
      // We only do it once per mount so navigating back to the welcome view
      // with the back button doesn't get hijacked.
      if (!this.openChatDeepLinkConsumed && this.router.url.includes('openChat=true')) {
        this.openChatDeepLinkConsumed = true;
        this.enterThread();
      }
      // Otherwise stay on the welcome screen — thread positioning + markRead
      // happen when the user actually enters the conversation (enterThread).
    });
  }

  // Guard so we only auto-enter the thread on the first `loadInitial`. Prevents
  // re-jumping into the thread if `loadInitial` ever runs again (e.g. on a
  // resync) while the URL still has the deep-link flag.
  private openChatDeepLinkConsumed = false;

  // Switches from the welcome screen to the conversation. Defers scroll
  // measurement until after Angular has rendered the thread, then jumps
  // to the first unread message (or to the bottom) and marks the thread
  // as read.
  enterThread(): void {
    this.view.set('thread');
    this.afterRenderSettled(() => {
      this.scrollToInitialPosition();
      void this.store.markRead();
    });
  }

  // Schedules `fn` after the next paint and a second rAF so any
  // images/attachments that synchronously affect layout have committed
  // before measurement. Falls back to setTimeout when rAF is unavailable.
  private afterRenderSettled(fn: () => void) {
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(performance.now()), 16);
    raf(() => raf(() => fn()));
  }

  // When the chat opens with unread admin messages, position the viewport at
  // the first unread one (so the user sees where they left off, with their
  // last-read context just above). If there are no unread messages, jump
  // straight to the bottom as usual. If the container hasn't been laid out
  // yet (scrollHeight still ~0), retry on the following frame.
  //
  // `targetMessageId` is captured on the first call (before markRead clears
  // the unread state) so that any rAF-deferred retries still know which
  // bubble to land on.
  private scrollToInitialPosition(retries: number = 3, targetMessageId?: string | null) {
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;
    if (targetMessageId === undefined) {
      if (this.store.unreadCount() > 0) {
        const firstUnread = this.displayMessages().find(
          (m) => m.from === 'them' && m.status !== ChatMessageStatus.READ
        );
        targetMessageId = firstUnread?._id ?? firstUnread?.id ?? null;
      } else {
        targetMessageId = null;
      }
    }
    const hasContent =
      container.scrollHeight > container.clientHeight + 4 || this.displayMessages().length === 0;
    if (!hasContent && retries > 0) {
      this.afterRenderSettled(() => this.scrollToInitialPosition(retries - 1, targetMessageId));
      return;
    }
    if (targetMessageId) {
      const target = container.querySelector(
        `[data-msg-id="${CSS.escape(targetMessageId)}"]`
      ) as HTMLElement | null;
      if (target) {
        // Land the first unread bubble near the top so the previous
        // (read) message peeks above it as a contextual anchor.
        const offsetTop = target.offsetTop - 24;
        container.scrollTo({ top: Math.max(0, offsetTop), behavior: 'auto' });
        return;
      }
    }
    this.scrollToBottom(false);
  }

  ngOnDestroy() {
    this.attachmentObserver?.disconnect();
    this.pickedFiles().forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    this.blobUrlCache.forEach((url) => url && URL.revokeObjectURL(url));
    this.blobUrlCache.clear();
    if (this.compressionWorker) {
      this.compressionWorker.terminate();
      this.compressionWorker = null;
    }
    // Tear down any in-flight recording so the mic doesn't stay green after
    // the user closes the chat.
    this.teardownRecorder();
    if (this.recordingPreviewUrl()) {
      URL.revokeObjectURL(this.recordingPreviewUrl()!);
    }
    // Avoid the store thinking the user is still actively watching messages
    // after the component is gone (e.g. floating widget unmounted, navigated
    // away). Otherwise auto-markRead-on-arrival would still fire.
    this.store.setViewerInThread(false);
  }

  goBack() {
    // Within the floating chat, the back arrow first peels back from the
    // thread to the welcome screen instead of closing the widget outright.
    if (this.view() === 'thread') {
      this.view.set('welcome');
      return;
    }
    if (this.embedded()) {
      this.close.emit();
      return;
    }
    this.location.back();
  }

  onDraftChange(value: string) {
    this.draft.set(value);
    if (value.trim().length > 0) this.store.notifyTyping();
  }

  onPickFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length === 0) return;
    void this.acceptFiles(files);
  }

  // Single entry point used by both the file picker and the paste handler.
  // Validates → compresses images in parallel → appends to pickedFiles.
  private async acceptFiles(files: File[]) {
    const remaining = MAX_FILES_PER_MESSAGE - this.pickedFiles().length;
    if (remaining <= 0) {
      this.snackBar.open(`Máximo ${MAX_FILES_PER_MESSAGE} archivos por mensaje.`, 'Ok', {
        duration: 4000
      });
      return;
    }
    const accepted: Array<{ file: File; previewUrl?: string }> = [];
    for (const file of files.slice(0, remaining)) {
      const processed = await this.processSingleFile(file);
      if (processed) accepted.push(processed);
    }
    if (accepted.length > 0) {
      this.pickedFiles.update((prev) => [...prev, ...accepted]);
    }
  }

  private async processSingleFile(file: File): Promise<{ file: File; previewUrl?: string } | null> {
    if (!ALLOWED_MIMES.includes(file.type)) {
      this.snackBar.open('Tipo de archivo no permitido.', 'Ok', { duration: 4000 });
      return null;
    }
    const isImage = file.type.startsWith('image/');
    if (isImage && file.size > MAX_RAW_IMAGE_BYTES) {
      this.snackBar.open('La imagen es demasiado grande para procesarla.', 'Ok', {
        duration: 4000
      });
      return null;
    }
    if (!isImage && file.size > MAX_FILE_BYTES) {
      this.snackBar.open('El archivo supera los 20MB permitidos.', 'Ok', { duration: 4000 });
      return null;
    }
    let finalFile = file;
    if (isImage) {
      finalFile = await this.compressImage(file);
      if (finalFile.size > MAX_FILE_BYTES) {
        this.snackBar.open('La imagen sigue superando los 20MB incluso comprimida.', 'Ok', {
          duration: 4000
        });
        return null;
      }
    }
    return {
      file: finalFile,
      previewUrl: finalFile.type.startsWith('image/') ? URL.createObjectURL(finalFile) : undefined
    };
  }

  private compressionWorker: Worker | null = null;
  private compressionReqId = 0;
  private compressionPending = new Map<number, { resolve: (blob: Blob | null) => void }>();

  private getCompressionWorker(): Worker | null {
    if (this.compressionWorker) return this.compressionWorker;
    if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return null;
    try {
      const worker = new Worker(new URL('./workers/image-compression.worker', import.meta.url), {
        type: 'module'
      });
      worker.addEventListener('message', (event: MessageEvent<any>) => {
        const data = event.data;
        const entry = this.compressionPending.get(data.id);
        if (!entry) return;
        this.compressionPending.delete(data.id);
        entry.resolve(data.ok ? (data.blob as Blob) : null);
      });
      worker.addEventListener('error', () => {
        this.compressionPending.forEach((p) => p.resolve(null));
        this.compressionPending.clear();
      });
      this.compressionWorker = worker;
      return worker;
    } catch {
      return null;
    }
  }

  private compressInWorker(file: File): Promise<Blob | null> {
    const worker = this.getCompressionWorker();
    if (!worker) return Promise.resolve(null);
    const id = ++this.compressionReqId;
    return new Promise<Blob | null>((resolve) => {
      this.compressionPending.set(id, { resolve });
      worker.postMessage({
        id,
        file,
        maxDimension: IMAGE_MAX_DIMENSION,
        quality: IMAGE_QUALITY
      });
    });
  }

  // Re-encodes the image as JPEG/85% with a max edge of 1920px. Tries the
  // Web Worker path first (OffscreenCanvas) so big photos don't block the
  // UI; falls back to in-page canvas when the worker isn't usable.
  // Returns the original file when compression doesn't beat the source.
  private async compressImage(file: File): Promise<File> {
    if (!file.type.startsWith('image/')) return file;
    if (file.type === 'image/gif') return file;
    if (file.size <= IMAGE_COMPRESS_THRESHOLD) return file;

    const workerBlob = await this.compressInWorker(file);
    if (workerBlob && workerBlob.size < file.size) {
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
      return new File([workerBlob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
    }

    try {
      const source = await this.loadImageSource(file);
      const srcW = 'width' in source ? source.width : (source as HTMLImageElement).naturalWidth;
      const srcH = 'height' in source ? source.height : (source as HTMLImageElement).naturalHeight;
      if (!srcW || !srcH) return file;

      const scale = Math.min(1, IMAGE_MAX_DIMENSION / Math.max(srcW, srcH));
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      // Flatten transparency to white (JPEG has no alpha channel).
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(source as CanvasImageSource, 0, 0, w, h);
      if (source instanceof ImageBitmap) source.close();

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', IMAGE_QUALITY);
      });
      if (!blob) return file;
      if (blob.size >= file.size) return file;

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
    } catch {
      return file;
    }
  }

  private async loadImageSource(file: File): Promise<ImageBitmap | HTMLImageElement> {
    if (typeof createImageBitmap === 'function') {
      try {
        return await createImageBitmap(file);
      } catch {
        // fall through to <img> fallback (Safari quirks etc.)
      }
    }
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image load failed'));
      };
      img.src = url;
    });
  }

  clearPickedFile(index?: number) {
    if (index == null) {
      this.pickedFiles().forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      this.pickedFiles.set([]);
      return;
    }
    this.pickedFiles.update((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }

  openFilePicker() {
    this.fileInput?.nativeElement.click();
  }

  async onSubmit() {
    if (this.store.sending()) return;
    const text = this.draft();
    const files = this.pickedFiles().map((p) => p.file);
    const editingId = this.editingMessageId();

    if (editingId) {
      if (!text.trim()) return;
      this.draft.set('');
      this.editingMessageId.set(null);
      await this.store.editMessage(editingId, text.trim());
      return;
    }

    if (!text.trim() && files.length === 0) return;
    this.draft.set('');
    this.clearPickedFile();
    await this.store.send(text, files);
    queueMicrotask(() => this.scrollToBottom(true));
  }

  // ── voice recording ─────────────────────────────────────
  // Recorder lifecycle:
  //   idle  ── tap mic ──▶ recording ── tap stop ──▶ preview
  //                                ╰── tap trash ──▶ idle
  //   preview ── tap send ──▶ idle (upload + send)
  //   preview ── tap trash ──▶ idle
  // We deliberately use a tap-to-toggle flow (not press-and-hold) because
  // hold gestures are flaky on touch devices that interpret a long-press as
  // text selection / context menu.
  async toggleRecording() {
    if (this.recordingState() === 'idle') {
      await this.startRecording();
    } else if (this.recordingState() === 'recording') {
      this.stopRecording();
    }
  }

  private async startRecording() {
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.snackBar.open('Tu navegador no permite grabar audio.', 'Ok', { duration: 4000 });
      return;
    }
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.snackBar.open('Permite el acceso al micrófono para grabar.', 'Ok', { duration: 4000 });
      return;
    }
    const mime = RECORDER_MIMES.find((m) => MediaRecorder.isTypeSupported?.(m)) ?? '';
    this.recordingPickedMime = mime;
    try {
      this.mediaRecorder = new MediaRecorder(
        this.mediaStream,
        mime ? { mimeType: mime } : undefined
      );
    } catch {
      this.snackBar.open('No se pudo iniciar la grabación.', 'Ok', { duration: 4000 });
      this.releaseStream();
      return;
    }
    this.recordedChunks = [];
    this.recordingCancelled = false;
    this.mediaRecorder.addEventListener('dataavailable', (e) => {
      if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
    });
    this.mediaRecorder.addEventListener('stop', () => this.handleRecorderStop());
    try {
      this.mediaRecorder.start();
    } catch {
      this.snackBar.open('No se pudo iniciar la grabación.', 'Ok', { duration: 4000 });
      this.teardownRecorder();
      return;
    }
    this.recordingStartedAt = Date.now();
    this.recordingElapsedMs.set(0);
    this.recordingState.set('recording');
    this.recordingTimer = setInterval(() => {
      const elapsed = Date.now() - this.recordingStartedAt;
      this.recordingElapsedMs.set(elapsed);
      if (elapsed >= MAX_RECORDING_MS) this.stopRecording();
    }, 250);
  }

  // Stops the recorder and transitions to preview state. The actual blob is
  // assembled in `handleRecorderStop` once MediaRecorder flushes its buffer.
  private stopRecording() {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // ignore — handleRecorderStop is also wired to the stop event
      }
    }
  }

  cancelRecording() {
    this.recordingCancelled = true;
    if (this.recordingState() === 'recording') {
      this.stopRecording();
    } else if (this.recordingState() === 'preview') {
      this.discardPreview();
    }
  }

  discardPreview() {
    const url = this.recordingPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.recordingBlob.set(null);
    this.recordingPreviewUrl.set(null);
    this.recordingDurationSec.set(0);
    this.recordingElapsedMs.set(0);
    this.recordingState.set('idle');
  }

  private handleRecorderStop() {
    const elapsedMs = Date.now() - this.recordingStartedAt;
    const durationSec = Math.max(1, Math.round(elapsedMs / 1000));
    const mime = this.recordingPickedMime || this.recordedChunks[0]?.type || 'audio/webm';
    const blob = new Blob(this.recordedChunks, { type: mime });
    this.recordedChunks = [];
    this.releaseStream();
    if (this.recordingCancelled || blob.size === 0) {
      this.recordingCancelled = false;
      this.recordingState.set('idle');
      return;
    }
    const url = URL.createObjectURL(blob);
    this.recordingBlob.set(blob);
    this.recordingPreviewUrl.set(url);
    this.recordingDurationSec.set(durationSec);
    this.recordingState.set('preview');
  }

  // Send the previewed recording as a customer message. Wraps the blob into
  // a File so the regular FormData upload path handles it.
  async sendRecording() {
    if (this.store.sending()) return;
    const blob = this.recordingBlob();
    const durationSec = this.recordingDurationSec();
    if (!blob) return;
    const ext = this.audioExtensionFor(blob.type);
    const file = new File([blob], `voice-${Date.now()}${ext}`, {
      type: blob.type,
      lastModified: Date.now()
    });
    const previewUrl = this.recordingPreviewUrl();
    // Clean up local state synchronously so the UI returns to idle while the
    // upload is in flight. The optimistic message gets its own blob URL inside
    // the store.
    this.recordingBlob.set(null);
    this.recordingPreviewUrl.set(null);
    this.recordingDurationSec.set(0);
    this.recordingElapsedMs.set(0);
    this.recordingState.set('idle');
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    await this.store.send(undefined, [file], durationSec);
    queueMicrotask(() => this.scrollToBottom(true));
  }

  formatRecordingClock(ms: number): string {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  private audioExtensionFor(mime: string): string {
    if (mime.includes('ogg')) return '.ogg';
    if (mime.includes('webm')) return '.webm';
    if (mime.includes('mp4') || mime.includes('aac')) return '.m4a';
    if (mime.includes('mpeg')) return '.mp3';
    if (mime.includes('wav')) return '.wav';
    return '';
  }

  private releaseStream() {
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
  }

  private teardownRecorder() {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // ignore
      }
    }
    this.mediaRecorder = null;
    this.releaseStream();
    this.recordedChunks = [];
    this.recordingCancelled = false;
  }

  beginEdit(message: { _id?: string; id?: string; text?: string }) {
    const id = message._id ?? message.id;
    if (!id) return;
    this.editingMessageId.set(id);
    this.draft.set(message.text ?? '');
  }

  cancelEdit() {
    this.editingMessageId.set(null);
    this.draft.set('');
  }

  async deleteOwnMessage(message: { _id?: string; id?: string }) {
    const id = message._id ?? message.id;
    if (!id) return;
    await this.store.deleteMessage(id);
  }

  async retryMessage(message: { _id?: string; id?: string; localId?: string }) {
    const id = message.localId ?? message._id ?? message.id;
    if (!id) return;
    await this.store.retry(id);
    queueMicrotask(() => this.scrollToBottom(true));
  }

  canEditOrDelete(createdAt: string): boolean {
    const age = Date.now() - new Date(createdAt).getTime();
    return age <= MESSAGE_EDIT_WINDOW_MS;
  }

  // Search bar UX
  toggleSearch() {
    const open = !this.searchOpen();
    this.searchOpen.set(open);
    if (!open) {
      this.searchQuery.set('');
      this.searchResults.set([]);
    }
  }

  onSearchInput(value: string) {
    this.searchQuery.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    if (!value.trim()) {
      this.searchResults.set([]);
      return;
    }
    this.searchDebounce = setTimeout(() => {
      void this.runSearch();
    }, 300);
  }

  private async runSearch() {
    const q = this.searchQuery().trim();
    if (!q) return;
    this.searching.set(true);
    try {
      const res = await this.chatService.search(q);
      this.searchResults.set(res.data?.messages ?? []);
    } finally {
      this.searching.set(false);
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.onSubmit();
    }
  }

  async onStartNewConversation() {
    if (this.store.startingNewConversation()) return;
    await this.store.startNewConversation();
    queueMicrotask(() => this.scrollToBottom(false));
  }

  // Scrolls the thread so the bubble with the given id is visible. Used
  // when the user taps a reply-quote preview. If the referenced message is
  // not currently loaded (older than the loaded page) we surface a snackbar
  // pointing the user at the "Cargar mensajes anteriores" button.
  scrollToMessage(messageId: string | undefined) {
    if (!messageId) return;
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;
    const target = container.querySelector(
      `[data-msg-id="${CSS.escape(messageId)}"]`
    ) as HTMLElement | null;
    if (!target) {
      this.snackBar.open(
        'Ese mensaje todavía no está cargado, desplazate hacia arriba y carga mensajes anteriores.',
        'Ok',
        { duration: 4000 }
      );
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('highlight-target');
    setTimeout(() => target.classList.remove('highlight-target'), 1600);
  }

  replyPreviewText(reply: NonNullable<ChatMessage['replyTo']>): string {
    if (reply.text && reply.text.trim()) {
      const t = reply.text.trim();
      return t.length > 120 ? t.slice(0, 117) + '…' : t;
    }
    if (reply.hasAttachments) return '📎 Adjunto';
    return 'Mensaje';
  }

  replyPreviewLabel(reply: NonNullable<ChatMessage['replyTo']>): string {
    return reply.sender === ChatSender.ADMIN ? 'Soporte Superpay23' : 'Tú';
  }

  onTicketMarkPaid(message: { ticket?: ChatTicket }) {
    // Tell support the customer just paid. We piggy-back on the regular
    // text-message path: cheap, no new endpoint needed, and the admin sees
    // it in the topic alongside the ticket.
    const ref = message.ticket?.reference;
    const note = `🙋 Ya envié el pago${ref ? ` del ticket #${ref}` : ''}.`;
    void this.store.send(note, []);
  }

  onTicketViewDetails(message: { ticket?: ChatTicket }) {
    // Reuse the chat-attachment dialog as a simple "details" pane.
    const t = message.ticket;
    if (!t) return;
    const lines: string[] = [];
    lines.push(`Estado: ${t.status}`);
    lines.push(`Monto: ${t.amount} ${t.currency}`);
    if (t.network) lines.push(`Red: ${t.network}`);
    if (t.walletAddress) lines.push(`Dirección: ${t.walletAddress}`);
    if (t.fiatEquivalent)
      lines.push(`Equivalente: ${t.fiatEquivalent.amount} ${t.fiatEquivalent.currency}`);
    if (t.reference) lines.push(`Referencia: ${t.reference}`);
    if (t.expiresAt) lines.push(`Expira: ${new Date(t.expiresAt).toLocaleString('es-CU')}`);
    if (t.note) lines.push(`Nota: ${t.note}`);
    this.snackBar.open(lines.join(' · '), 'Cerrar', { duration: 6000 });
  }

  async copyToClipboard(value: string) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(ta);
        }
      }
      this.snackBar.open('Copiado', 'Ok', { duration: 1500 });
    } catch {
      this.snackBar.open('No se pudo copiar.', 'Ok', { duration: 2500 });
    }
  }

  onScroll() {
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;
    if (container.scrollTop <= 40) {
      void this.handleLoadMore();
    }
    const nearBottom = this.isNearBottom(140);
    if (nearBottom) {
      this.showScrollFab.set(false);
      this.newMessagesInFab.set(0);
    } else {
      this.showScrollFab.set(true);
    }
  }

  // Preserves the user's scroll position while loadMore prepends older
  // messages at the top of the list — without this, the viewport jumps.
  async handleLoadMore() {
    const container = this.messagesContainer?.nativeElement;
    if (!container) {
      void this.store.loadMore();
      return;
    }
    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    await this.store.loadMore();
    queueMicrotask(() => {
      const newScrollHeight = container.scrollHeight;
      const delta = newScrollHeight - prevScrollHeight;
      if (delta > 0) {
        container.scrollTop = prevScrollTop + delta;
      }
    });
  }

  jumpToBottom() {
    this.showScrollFab.set(false);
    this.newMessagesInFab.set(0);
    this.scrollToBottom(true);
  }

  onPaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (!items?.length) return;
    const pasted: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file') continue;
      const file = item.getAsFile();
      if (!file) continue;
      if (!ALLOWED_MIMES.includes(file.type)) continue;
      pasted.push(file);
    }
    if (pasted.length > 0) {
      event.preventDefault();
      void this.acceptFiles(pasted);
    }
  }

  /**
   * Returns the cached blob URL for an attachment, or '' if not yet loaded.
   * Does NOT trigger a fetch — use `requestAttachmentLoad()` to start downloading.
   */
  attachmentHref(objectKey: string): string {
    if (!objectKey) return '';
    this.blobCacheVersion();
    return this.blobUrlCache.get(objectKey) ?? '';
  }

  /**
   * Triggers the download of an attachment blob on-demand.
   * Called by IntersectionObserver (images) or on first play (audio).
   */
  requestAttachmentLoad(objectKey: string): void {
    if (!objectKey) return;
    if (this.blobUrlCache.has(objectKey)) return;
    if (this.blobUrlInFlight.has(objectKey)) return;
    this.blobUrlInFlight.add(objectKey);
    this.chatService.downloadAttachment(objectKey).then((res) => {
      const blob = res.data as Blob | undefined;
      if (blob) {
        this.blobUrlCache.set(objectKey, URL.createObjectURL(blob));
        this.blobCacheVersion.update((v) => v + 1);
      }
      // Missing blob: leave the key uncached so a later request can retry.
    }).catch(() => {
      // Transient failure: don't cache it as terminal — allow retries.
    })
      .finally(() => this.blobUrlInFlight.delete(objectKey));
  }

  /**
   * href for a non-image file attachment, or `null` while the blob is still
   * loading. Returning `null` (not '') keeps the attribute off the <a> so the
   * browser treats it as a non-navigable placeholder instead of a link to the
   * current page.
   */
  fileAttachmentHref(objectKey: string): string | null {
    if (!objectKey) return null;
    return this.attachmentHref(objectKey) || null;
  }

  /**
   * Handles clicks on a file attachment link. When the blob hasn't been
   * downloaded yet the <a> has no href, so we cancel the (empty) navigation
   * and trigger the lazy load; once cached, the version bump re-renders the
   * href and a follow-up click opens it in a new tab natively.
   */
  onFileAttachmentClick(event: MouseEvent, objectKey: string): void {
    if (!objectKey) return;
    if (!this.attachmentHref(objectKey)) {
      event.preventDefault();
      this.requestAttachmentLoad(objectKey);
    }
  }

  // ── Lazy-load images via IntersectionObserver ──
  private attachmentObserver?: IntersectionObserver;
  private readonly observedElements = new WeakSet<HTMLElement>();

  private getAttachmentObserver(): IntersectionObserver {
    if (!this.attachmentObserver) {
      this.attachmentObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const key = (entry.target as HTMLElement).dataset['objectKey'];
              if (key) this.requestAttachmentLoad(key);
              this.attachmentObserver!.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '200px' }
      );
    }
    return this.attachmentObserver;
  }

  /** Called from template to observe an image/file element for lazy loading. */
  observeAttachment(el: HTMLElement | null, objectKey: string): string | null {
    if (!el || !objectKey) return null;
    if (this.blobUrlCache.has(objectKey)) return null;
    if (this.observedElements.has(el)) return null;
    this.observedElements.add(el);
    el.dataset['objectKey'] = objectKey;
    this.getAttachmentObserver().observe(el);
    return '';
  }

  async openImage(att: ChatAttachment) {
    if (att.kind !== AttachmentKind.IMAGE) return;

    // Optimistic message still uploading → the local blob URL is the only
    // thing we have, but it is already a blob: URL so the viewer accepts it.
    if (!att.minioObjectKey && att.localPreviewUrl) {
      this.openInPreviewDialog(att.localPreviewUrl, att, false);
      return;
    }

    if (!att.minioObjectKey) return;
    if (this.openingAttachmentKey() === att.minioObjectKey) return;
    this.openingAttachmentKey.set(att.minioObjectKey);
    try {
      const res = await this.chatService.downloadAttachment(att.minioObjectKey);
      const blob = res.data as Blob | undefined;
      if (!blob) {
        this.snackBar.open('No se pudo abrir la imagen.', 'Ok', { duration: 4000 });
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      this.openInPreviewDialog(objectUrl, att, true);
    } catch {
      this.snackBar.open('No se pudo abrir la imagen.', 'Ok', { duration: 4000 });
    } finally {
      this.openingAttachmentKey.set(null);
    }
  }

  private openInPreviewDialog(url: string, att: ChatAttachment, revokeOnClose: boolean) {
    const ref = this.dialog.open(InvoicePreviewDialogComponent, {
      data: {
        url,
        isImage: true,
        fileName: att.originalName || 'imagen'
      },
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'invoice-preview-dialog'
    });
    if (revokeOnClose) {
      ref.afterClosed().subscribe(() => URL.revokeObjectURL(url));
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatTime(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('es-CU', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  private formatDayLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    if (sameDay(date, today)) return 'Hoy';
    if (sameDay(date, yesterday)) return 'Ayer';
    const sameYear = date.getFullYear() === today.getFullYear();
    return sameYear
      ? date.toLocaleDateString('es', { day: '2-digit', month: 'short' })
      : date.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  trackMessage(_index: number, message: DisplayMessage): string {
    return (message._id ?? message.id ?? message.localId ?? '') as string;
  }

  hasAudioAttachment(message: DisplayMessage): boolean {
    return !!message.attachments?.some((a) => a.kind === AttachmentKind.AUDIO);
  }

  // Customer just pressed play on a voice note authored by support. We
  // notify the backend so the bot can react to the operator's Telegram
  // message with 🎧 — gives the agent a stronger signal than the 👀 placed
  // by markRead. Only fires for admin messages; our own recordings don't
  // need a played-back receipt. The store dedupes per session.
  onAdminAudioPlayed(message: DisplayMessage) {
    if (message.from !== 'them') return;
    const id = message._id ?? message.id;
    if (!id) return;
    void this.store.markAudioPlayed(id);
  }

  // Maps the internal ChatMessageStatus enum to the string the audio player
  // component understands. Kept in the host component so the player stays
  // domain-agnostic (it just renders the right icon for whatever string it
  // gets).
  audioBubbleStatus(
    status: ChatMessageStatus
  ): 'sending' | 'sent' | 'delivered' | 'read' | 'failed' {
    switch (status) {
      case ChatMessageStatus.SENDING:
        return 'sending';
      case ChatMessageStatus.SENT:
        return 'sent';
      case ChatMessageStatus.DELIVERED:
        return 'delivered';
      case ChatMessageStatus.READ:
        return 'read';
      case ChatMessageStatus.FAILED:
        return 'failed';
      default:
        return 'sent';
    }
  }

  private scrollToBottom(smooth = false) {
    const container = this.messagesContainer?.nativeElement;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }

  private isNearBottom(threshold = 120): boolean {
    const container = this.messagesContainer?.nativeElement;
    if (!container) return true;
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    return distance <= threshold;
  }
}
